import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BASE_PATH } from './variables';

export interface PriceBook {
  id: number;
  code: string;
  name: string;
  description?: string;
  currencyCode?: string;
  status?: string;
  channelCode?: string;
}

export interface PriceItem {
  id: number;
  priceBookId: number;
  priceBookCode?: string;
  revisionId: number;
  skuCode: string;
  skuName?: string;
  priceType: string;
  amount: number;
  currencyCode?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  startDate?: string | null;
  endDate?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  scheduleStatus?: string | null;
  status?: string;
  publishedFlag?: boolean;
  ruleJson?: string;
  conditionJson?: string;
  channelCode?: string;
  vendorId?: number;
  primaryCostFlag?: boolean;
  minQty?: number;
  customerGroupCode?: string;
  campaignId?: number | null;
  campaignName?: string | null;
  pendingAmount?: number | null;
  pendingAmountStatus?: string | null;
  changeReason?: string | null;
  marketSource?: string | null;
  scopeType?: string | null;
  physicalShopIds?: number[] | null;
  physicalShopNames?: string[] | null;
}

export interface BasePriceAudit {
  id: number;
  skuCode: string;
  priceBookCode?: string;
  priceType?: string;
  action: 'CREATE' | 'UPDATE' | 'DEACTIVATE' | 'DELETE' | string;
  oldAmount?: number | null;
  newAmount?: number | null;
  currencyCode?: string;
  actor: string;
  changedAt: string;
  creationDate?: string;
  changeReason?: string | null;
  marketSource?: string | null;
}

export interface PriceAsOfLine {
  priceItemId?: number;
  priceBookCode?: string;
  priceType?: string;
  amount?: number;
  currencyCode?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  campaignName?: string | null;
  applied?: boolean;
}

export interface PriceAsOfResponse {
  skuCode: string;
  at: string;
  channelCode?: string;
  storeCode?: string | null;
  appliedPrice?: PriceAsOfLine | null;
  activeLines?: PriceAsOfLine[];
}

export interface BulkPriceItemSkip {
  itemId: number;
  reason?: string;
}

export interface BulkPriceItemActionResult {
  requested: number;
  succeededIds: number[];
  skipped: BulkPriceItemSkip[];
}

export interface PriceItemImportRowError {
  row: number;
  sheet?: string | null;
  message: string;
  field?: string | null;
}

export interface PriceItemImportResult {
  totalRows: number;
  successCount: number;
  failureCount: number;
  errors: PriceItemImportRowError[];
}

export interface BasePriceAuditPage {
  pageContent: BasePriceAudit[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface PriceItemPage {
  pageContent: PriceItem[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface PriceRevision {
  id: number;
  priceBookId: number;
  versionNo: number;
  status: string;
}

export interface EvaluatePriceLineRequest {
  lineNo?: string;
  skuId?: number;
  skuCode: string;
  quantity: number;
}

export interface EvaluatePriceRequest {
  currencyCode?: string;
  channelCode: string;
  storeCode?: string;
  couponCodes?: string[];
  selectedOfferIds?: number[];
  lines: EvaluatePriceLineRequest[];
}

export interface EvaluatePriceLine {
  lineNo?: string;
  skuCode: string;
  quantity?: number;
  originalUnitPrice?: number | null;
  channelUnitPrice?: number | null;
  finalUnitPrice?: number | null;
  finalLineAmount?: number | null;
  priceItemId?: number | null;
  revisionId?: number | null;
  status?: string;
}

export interface EvaluateOffer {
  offerId?: number | null;
  offerCode?: string | null;
  applied: boolean;
  discountAmount?: number | null;
  reason?: string;
}

export interface EvaluatePriceResponse {
  currencyCode?: string;
  channelCode?: string;
  lines: EvaluatePriceLine[];
  offers: EvaluateOffer[];
  totalDiscount?: number | null;
  finalAmount?: number | null;
  warnings?: string[];
}

export interface FloorMargin {
  marginType: 'PERCENT' | 'AMOUNT';
  marginValue: number;
  currencyCode?: string;
}

export interface SellingPricePolicyItem {
  code: string;
  enabled: boolean;
}

export interface PriceItemCreate {
  revisionId: number;
  skuCode: string;
  priceType: string;
  amount: number;
  currencyCode: string;
  effectiveFrom: string;
  startDate?: string;
  endDate?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  ruleJson?: string;
  status: string;
  minQty?: number;
  customerGroupCode?: string;
  changeReason: string;
  marketSource?: string | null;
  scopeType?: string | null;
  physicalShopIds?: number[] | null;
  physicalShopNames?: string[] | null;
  physicalShopCodes?: string[] | null;
}

@Injectable({ providedIn: 'root' })
export class PriceListApiService {
  private readonly http = inject(HttpClient);
  private readonly basePath = inject(BASE_PATH);

  listBooks(): Observable<PriceBook[]> {
    return this.http.get<PriceBook[]>(`${this.basePath}/v1/price-lists`);
  }

  pageItems(
    bookId: number,
    query: {
      page: number;
      size: number;
      skuCode?: string;
      skuName?: string;
      amountFrom?: number;
      amountTo?: number;
      priceType?: string;
      publishedOnly?: boolean;
      vendorId?: number;
      startDate?: string;
      endDate?: string;
      lifecycleStatus?: string;
    },
  ): Observable<PriceItemPage> {
    let params = new HttpParams()
      .set('page', query.page)
      .set('size', query.size);
    if (query.skuCode) {
      params = params.set('skuCode', query.skuCode);
    }
    if (query.skuName) {
      params = params.set('skuName', query.skuName);
    }
    if (query.amountFrom != null) {
      params = params.set('amountFrom', query.amountFrom);
    }
    if (query.amountTo != null) {
      params = params.set('amountTo', query.amountTo);
    }
    if (query.priceType) {
      params = params.set('priceType', query.priceType);
    }
    if (query.publishedOnly != null) {
      params = params.set('publishedOnly', query.publishedOnly);
    }
    if (query.vendorId != null) {
      params = params.set('vendorId', query.vendorId);
    }
    if (query.startDate) {
      params = params.set('startDate', query.startDate);
    }
    if (query.endDate) {
      params = params.set('endDate', query.endDate);
    }
    if (query.lifecycleStatus) {
      params = params.set('lifecycleStatus', query.lifecycleStatus);
    }
    return this.http.get<PriceItemPage>(
      `${this.basePath}/v1/price-lists/${bookId}/items`,
      { params },
    );
  }

  pricedSkuCodes(bookId: number, priceType: string): Observable<string[]> {
    const params = new HttpParams().set('priceType', priceType);
    return this.http.get<string[]>(
      `${this.basePath}/v1/price-lists/${bookId}/priced-sku-codes`,
      { params },
    );
  }

  listRevisions(bookId: number): Observable<PriceRevision[]> {
    return this.http.get<PriceRevision[]>(
      `${this.basePath}/v1/price-books/${bookId}/revisions`,
    );
  }

  createRevision(bookId: number, impactSummary: string): Observable<PriceRevision> {
    return this.http.post<PriceRevision>(
      `${this.basePath}/v1/price-books/${bookId}/revisions`,
      { impactSummary },
    );
  }

  createItem(bookId: number, body: PriceItemCreate): Observable<PriceItem> {
    return this.http.post<PriceItem>(
      `${this.basePath}/v1/price-books/${bookId}/items`,
      body,
    );
  }

  createBasePrice(bookId: number, body: PriceItemCreate): Observable<PriceItem> {
    return this.http.post<PriceItem>(
      `${this.basePath}/v1/price-books/${bookId}/base-prices`,
      body,
    );
  }

  updateEffectiveWindow(
    itemId: number,
    body: {
      startAt?: string | null;
      endAt?: string | null;
      startDate?: string | null;
      endDate?: string | null;
      changeReason: string;
    },
  ): Observable<PriceItem> {
    return this.http.put<PriceItem>(
      `${this.basePath}/v1/price-books/items/${itemId}/effective-window`,
      body,
    );
  }

  updateBasePrice(
    itemId: number,
    amount: number,
    changeReason: string,
  ): Observable<PriceItem> {
    return this.http.put<PriceItem>(
      `${this.basePath}/v1/price-books/items/${itemId}/base-price`,
      { amount, changeReason },
    );
  }

  lookupPriceAsOf(query: {
    skuCode: string;
    atLocal?: string;
    channelCode?: string;
    storeCode?: string;
  }): Observable<PriceAsOfResponse> {
    let params = new HttpParams().set('skuCode', query.skuCode);
    if (query.atLocal) {
      params = params.set('atLocal', query.atLocal);
    }
    if (query.channelCode) {
      params = params.set('channelCode', query.channelCode);
    }
    if (query.storeCode) {
      params = params.set('storeCode', query.storeCode);
    }
    return this.http.get<PriceAsOfResponse>(`${this.basePath}/v1/prices/as-of`, {
      params,
    });
  }

  pagePriceChanges(query: {
    page: number;
    size: number;
    skuCode?: string;
    skuName?: string;
    priceBookCode?: string;
    priceType?: string;
    actor?: string;
    startDate?: string;
    endDate?: string;
  }): Observable<BasePriceAuditPage> {
    let params = new HttpParams().set('page', query.page).set('size', query.size);
    if (query.skuCode) {
      params = params.set('skuCode', query.skuCode);
    }
    if (query.skuName) {
      params = params.set('skuName', query.skuName);
    }
    if (query.priceBookCode) {
      params = params.set('priceBookCode', query.priceBookCode);
    }
    if (query.priceType) {
      params = params.set('priceType', query.priceType);
    }
    if (query.actor) {
      params = params.set('actor', query.actor);
    }
    if (query.startDate) {
      params = params.set('startDate', query.startDate);
    }
    if (query.endDate) {
      params = params.set('endDate', query.endDate);
    }
    return this.http.get<BasePriceAuditPage>(`${this.basePath}/v1/price-changes`, {
      params,
    });
  }

  exportPriceChanges(query: {
    skuCode?: string;
    skuName?: string;
    priceBookCode?: string;
    priceType?: string;
    actor?: string;
    startDate?: string;
    endDate?: string;
    format: 'csv' | 'xlsx';
  }): Observable<Blob> {
    let params = new HttpParams().set('format', query.format);
    if (query.skuCode) {
      params = params.set('skuCode', query.skuCode);
    }
    if (query.skuName) {
      params = params.set('skuName', query.skuName);
    }
    if (query.priceBookCode) {
      params = params.set('priceBookCode', query.priceBookCode);
    }
    if (query.priceType) {
      params = params.set('priceType', query.priceType);
    }
    if (query.actor) {
      params = params.set('actor', query.actor);
    }
    if (query.startDate) {
      params = params.set('startDate', query.startDate);
    }
    if (query.endDate) {
      params = params.set('endDate', query.endDate);
    }
    return this.http.get(`${this.basePath}/v1/price-changes/export`, {
      params,
      responseType: 'blob',
    });
  }

  listSellingPrices(): Observable<SellingPricePolicyItem[]> {
    return this.http.get<SellingPricePolicyItem[]>(
      `${this.basePath}/v1/price-settings/selling-prices`,
    );
  }

  updateSellingPrices(
    items: SellingPricePolicyItem[],
  ): Observable<SellingPricePolicyItem[]> {
    return this.http.put<SellingPricePolicyItem[]>(
      `${this.basePath}/v1/price-settings/selling-prices`,
      { items },
    );
  }

  getFloorMargin(): Observable<FloorMargin> {
    return this.http.get<FloorMargin>(`${this.basePath}/v1/price-settings/floor-margin`);
  }

  updateFloorMargin(body: Pick<FloorMargin, 'marginType' | 'marginValue'>): Observable<FloorMargin> {
    return this.http.put<FloorMargin>(`${this.basePath}/v1/price-settings/floor-margin`, body);
  }

  pageBasePriceAudits(query: {
    page: number;
    size: number;
    skuCode?: string;
  }): Observable<BasePriceAuditPage> {
    let params = new HttpParams().set('page', query.page).set('size', query.size);
    if (query.skuCode) {
      params = params.set('skuCode', query.skuCode);
    }
    return this.http.get<BasePriceAuditPage>(
      `${this.basePath}/v1/price-lists/base-price-audits`,
      { params },
    );
  }

  submitRevision(revisionId: number): Observable<PriceRevision> {
    return this.http.post<PriceRevision>(
      `${this.basePath}/v1/price-revisions/${revisionId}/submit`,
      {},
    );
  }

  approveRevision(revisionId: number): Observable<PriceRevision> {
    return this.http.post<PriceRevision>(
      `${this.basePath}/v1/price-revisions/${revisionId}/approve`,
      {},
    );
  }

  publishRevision(revisionId: number): Observable<PriceRevision> {
    return this.http.post<PriceRevision>(
      `${this.basePath}/v1/price-revisions/${revisionId}/publish`,
      {},
    );
  }

  evaluate(body: EvaluatePriceRequest): Observable<EvaluatePriceResponse> {
    return this.http.post<EvaluatePriceResponse>(
      `${this.basePath}/v1/pricing/evaluate`,
      body,
    );
  }

  deactivateBasePrice(itemId: number): Observable<PriceItem> {
    return this.http.post<PriceItem>(
      `${this.basePath}/v1/price-books/items/${itemId}/deactivate`,
      {},
    );
  }

  deactivateItem(itemId: number): Observable<PriceItem> {
    return this.deactivateBasePrice(itemId);
  }

  approveItem(itemId: number): Observable<unknown> {
    return this.http.post(
      `${this.basePath}/v1/price-books/items/${itemId}/approve`,
      {},
    );
  }

  bulkApproveItems(itemIds: number[]): Observable<BulkPriceItemActionResult> {
    return this.http.post<BulkPriceItemActionResult>(
      `${this.basePath}/v1/price-books/items/bulk-approve`,
      { itemIds },
    );
  }

  bulkDeactivateItems(itemIds: number[]): Observable<BulkPriceItemActionResult> {
    return this.http.post<BulkPriceItemActionResult>(
      `${this.basePath}/v1/price-books/items/bulk-deactivate`,
      { itemIds },
    );
  }

  bulkCancelItems(itemIds: number[]): Observable<BulkPriceItemActionResult> {
    return this.http.post<BulkPriceItemActionResult>(
      `${this.basePath}/v1/price-books/items/bulk-cancel`,
      { itemIds },
    );
  }

  bulkDeleteItems(itemIds: number[]): Observable<BulkPriceItemActionResult> {
    return this.http.post<BulkPriceItemActionResult>(
      `${this.basePath}/v1/price-books/items/bulk-delete`,
      { itemIds },
    );
  }

  importPriceItems(file: File): Observable<PriceItemImportResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<PriceItemImportResult>(
      `${this.basePath}/v1/price-books/items/import`,
      form,
    );
  }

  updateItem(itemId: number, body: PriceItemCreate): Observable<PriceItem> {
    return this.http.put<PriceItem>(
      `${this.basePath}/v1/price-books/items/${itemId}`,
      body,
    );
  }

  deleteItem(itemId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.basePath}/v1/price-books/items/${itemId}`,
    );
  }
}
